import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Empty, Loader, PageHeader, btn } from "@/components/Page";
import { LeagueBadge } from "@/components/triumvirate/LeagueBadge";
import {
  LANES,
  TRIUMVIRATE_MODE,
  TriumvirateHeroPanel,
  TriumvirateSectionNav,
  RuleCard,
  formatDate,
} from "@/components/triumvirate/TriumvirateSectionChrome";

function FighterRow({ fighter, selected, lane, onToggle, onLaneChange }) {
  return (
    <div
      data-testid={`triumvirate-roster-card-${fighter.id}`}
      className={`border rounded-sm p-4 ${selected ? "border-amber-500/70 bg-amber-950/20" : "border-zinc-800 bg-zinc-950/65"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-cinzel text-lg text-amber-200">{fighter.name}</div>
          <div className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">{fighter.cls}</div>
        </div>
        <div className="text-right">
          <div data-testid={`triumvirate-roster-score-${fighter.id}`} className="font-cinzel text-lg text-amber-300 tabular-nums">
            {fighter.score.toLocaleString()}
          </div>
          <div className="text-[11px] text-zinc-500">individual score</div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`triumvirate-roster-skills-${fighter.id}`}>
        {Object.entries(fighter.effective_skills || {}).map(([key, value]) => (
          <span key={key} className="border border-zinc-800 px-2 py-1 text-[11px] text-zinc-400 rounded-sm">
            {key.slice(0, 3).toUpperCase()} {Number(value || 0)}
          </span>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid={`triumvirate-toggle-${fighter.id}`}
          onClick={onToggle}
          className={btn(selected ? "ghost" : "primary")}
        >
          {selected ? "Remove" : "Select"}
        </button>
        <label className="text-xs uppercase tracking-[0.16em] text-zinc-500">
          Lane
          <select
            data-testid={`triumvirate-lane-select-${fighter.id}`}
            value={lane || ""}
            disabled={!selected}
            onChange={(event) => onLaneChange(event.target.value)}
            className="ml-2 bg-zinc-950 border border-amber-900/35 px-2 py-2 text-sm text-amber-200 outline-none rounded-sm"
          >
            {LANES.map((entry) => (
              <option key={entry} value={entry}>
                {entry[0].toUpperCase() + entry.slice(1)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

function SquadMembers({ members, testIdPrefix }) {
  if (!members?.length) {
    return <Empty label="No squad saved yet." />;
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-members`}>
      {members.map((fighter) => (
        <div key={fighter.gladiator_id} data-testid={`${testIdPrefix}-fighter-${fighter.gladiator_id}`} className="border border-zinc-800 bg-zinc-950/55 p-3 rounded-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-cinzel text-base text-amber-200">{fighter.name}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">
                {fighter.cls} · {fighter.lane}
              </div>
            </div>
            <div data-testid={`${testIdPrefix}-fighter-score-${fighter.gladiator_id}`} className="text-sm font-semibold text-amber-300 tabular-nums">
              {Number(fighter.score || 0).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LudusTrias() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [laneMap, setLaneMap] = useState({});
  const [squadName, setSquadName] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [squadsRes, profileRes] = await Promise.all([
        api.get("/triumvirate/squads"),
        api.get("/triumvirate/profile"),
      ]);
      setPayload({
        squads: squadsRes.data,
        profile: profileRes.data,
      });
    } catch (error) {
      toast.error(apiErr(error, "Could not load Ludus Trias"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const squad = payload?.squads?.squad || payload?.squads?.squads?.overall || null;
  const roster = useMemo(() => payload?.squads?.available_gladiators || [], [payload?.squads?.available_gladiators]);
  const overview = payload?.squads?.overview || { invalid_squads: [], valid_squad_count: 0, has_saved_squad: false };
  const rewardConfig = payload?.squads?.reward_config || { leagues: {} };
  const selectedRewards = rewardConfig?.leagues?.overall?.placements || [];
  const ratings = payload?.profile?.ratings || payload?.squads?.ratings || {};
  const careerRatings = payload?.profile?.career_ratings || payload?.squads?.career_ratings || {};

  useEffect(() => {
    const savedGladiators = squad?.gladiators || [];
    const nextIds = savedGladiators.map((fighter) => fighter.gladiator_id);
    const nextLaneMap = {};
    savedGladiators.forEach((fighter) => {
      nextLaneMap[fighter.gladiator_id] = fighter.lane;
    });
    setSelectedIds(nextIds);
    setLaneMap(nextLaneMap);
    setSquadName(squad?.squad_name || "Ludus Trias Squad");
  }, [squad?.updated_at, squad?.gladiators]);

  const rosterById = useMemo(() => new Map(roster.map((fighter) => [fighter.id, fighter])), [roster]);
  const selectedFighters = useMemo(() => {
    return selectedIds
      .map((id) => rosterById.get(id) || (squad?.gladiators || []).find((fighter) => fighter.gladiator_id === id))
      .filter(Boolean)
      .sort((left, right) => {
        const leftId = left.id || left.gladiator_id;
        const rightId = right.id || right.gladiator_id;
        return LANES.indexOf(laneMap[leftId] || left.lane) - LANES.indexOf(laneMap[rightId] || right.lane);
      });
  }, [selectedIds, rosterById, laneMap, squad?.gladiators]);

  const selectedByLane = useMemo(() => {
    const map = {};
    selectedFighters.forEach((fighter) => {
      const fighterId = fighter.id || fighter.gladiator_id;
      const lane = laneMap[fighterId] || fighter.lane;
      if (lane) map[lane] = fighter;
    });
    return map;
  }, [selectedFighters, laneMap]);

  const squadScore = useMemo(
    () => selectedFighters.reduce((sum, fighter) => sum + Number(fighter.score || 0), 0),
    [selectedFighters]
  );

  const validationErrors = useMemo(() => {
    const errors = [];
    if (selectedIds.length !== 3) errors.push("Pick exactly three gladiators.");
    const lanesChosen = selectedIds.map((id) => laneMap[id]).filter(Boolean);
    if (lanesChosen.length !== 3 || new Set(lanesChosen).size !== 3) errors.push("Assign Left, Center, and Right once each.");
    return errors;
  }, [laneMap, selectedIds]);

  const toggleFighter = (fighter) => {
    const id = fighter.id;
    if (selectedIds.includes(id)) {
      setSelectedIds((current) => current.filter((entry) => entry !== id));
      setLaneMap((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      return;
    }
    if (selectedIds.length >= 3) {
      toast.error("A Ludus Trias squad can only hold three gladiators.");
      return;
    }
    const taken = new Set(Object.values(laneMap));
    const nextLane = LANES.find((entry) => !taken.has(entry)) || LANES[0];
    setSelectedIds((current) => [...current, id]);
    setLaneMap((current) => ({ ...current, [id]: current[id] || nextLane }));
  };

  const saveSquad = async () => {
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }
    setSaving(true);
    try {
      await api.post("/triumvirate/squads/save", {
        gladiators: selectedIds.map((id) => ({ gladiator_id: id, lane: laneMap[id] })),
        squad_name: squadName,
      });
      toast.success("Ludus Trias squad saved.");
      await load(true);
    } catch (error) {
      toast.error(apiErr(error, "Could not save squad"));
    } finally {
      setSaving(false);
    }
  };

  const deleteSquad = async () => {
    if (!squad?.exists) return;
    if (!window.confirm("Clear your saved Ludus Trias squad?")) return;
    setDeleting(true);
    try {
      await api.delete("/triumvirate/squads/overall");
      toast.success("Squad cleared.");
      await load(true);
    } catch (error) {
      toast.error(apiErr(error, "Could not clear squad"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading && !payload) return <Loader label="Opening Ludus Trias…" />;

  return (
    <div data-testid="triumvirate-page" className="space-y-6">
      <PageHeader
        title="Ludus Trias"
        subtitle="One live-scored 3v3 mode, one saved squad, and one monthly ladder shared by everyone."
        action={
          <div className="flex flex-wrap gap-2">
            <Link data-testid="triumvirate-archive-link" to="/ludus-trias/archive" className={btn("ghost")}>
              Season Archive
            </Link>
            <button data-testid="triumvirate-refresh-button" type="button" onClick={() => load()} className={btn("ghost")}>
              Refresh
            </button>
          </div>
        }
      />

      <TriumvirateHeroPanel
        pageKey="triumvirate"
        title="Build the one squad that carries your whole live-scored Ludus Trias run."
        copy="Your saved three-gladiator lineup updates automatically as gear and stats change. There are no brackets to qualify for anymore — just one shared ladder and one live squad score."
        ratings={ratings}
        careerRatings={careerRatings}
        season={payload?.profile?.season || payload?.squads?.season}
      />

      <TriumvirateSectionNav />

      {overview.invalid_squads?.length > 0 && (
        <section data-testid="triumvirate-invalid-squad-banner" className="border border-red-900/45 bg-red-950/20 rounded-sm p-4">
          <div className="font-cinzel text-lg text-red-300">Squad attention needed</div>
          <div className="mt-2 space-y-2">
            {overview.invalid_squads.map((entry, index) => (
              <div key={`${entry.label}-${index}`} data-testid={`triumvirate-invalid-squad-${index}`} className="text-sm text-red-200">
                <span className="font-semibold">{entry.label}:</span> {entry.validation_errors?.[0] || "This squad needs attention."}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid gap-4 lg:grid-cols-3">
        <RuleCard testId="triumvirate-rule-score" title="Live score" body="Ludus Trias uses the average of your three gladiators’ current effective stats with gear." />
        <RuleCard testId="triumvirate-rule-squad" title="One saved squad" body="You keep one saved 3v3 squad for both attack and defense. Change the lineup here." />
        <RuleCard testId="triumvirate-rule-beasts" title="Battle rules" body="Beasts are disabled. Fights use shared turn order and lane-based targeting." />
      </section>

      <section className="space-y-6">
        <div data-testid="triumvirate-builder-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5 sm:p-6 lg:p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-cinzel text-3xl text-amber-200">Build Your Ludus Trias Squad</h3>
                <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-builder-badge" />
              </div>
              <p className="mt-2 max-w-2xl text-sm text-zinc-500">Pick exactly three gladiators, assign Left, Center, and Right, and save the one squad used for both attacking and defending.</p>
            </div>
            <div className="text-right">
              <div data-testid="triumvirate-builder-score" className="font-cinzel text-4xl text-amber-300 tabular-nums">
                {squadScore.toLocaleString()}
              </div>
              <div data-testid="triumvirate-builder-score-label" className="text-xs uppercase tracking-[0.16em] text-zinc-500">
                average squad score
              </div>
            </div>
          </div>

          <label className="mt-5 block max-w-md"><span className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Squad name</span><input data-testid="triumvirate-squad-name-input" value={squadName} onChange={(event) => setSquadName(event.target.value.slice(0, 32))} maxLength={32} placeholder="Ludus Trias Squad" className="mt-2 w-full rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none focus:border-amber-700" /></label>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            {LANES.map((laneName) => {
              const fighter = selectedByLane[laneName];
              return (
                <div key={laneName} data-testid={`triumvirate-builder-lane-${laneName}`} className="border border-zinc-800 bg-zinc-950/55 rounded-sm p-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{laneName} lane</div>
                  {fighter ? (
                    <>
                      <div className="mt-2 font-cinzel text-lg text-amber-200">{fighter.name}</div>
                      <div className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">{fighter.cls}</div>
                      <div className="mt-3 text-sm text-amber-300 tabular-nums">{Number(fighter.score || 0).toLocaleString()} score</div>
                    </>
                  ) : (
                    <div className="mt-3 text-sm text-zinc-500">Unassigned</div>
                  )}
                </div>
              );
            })}
          </div>

          {validationErrors.length > 0 && (
            <div data-testid="triumvirate-builder-errors" className="mt-5 border border-red-900/45 bg-red-950/20 p-4 rounded-sm space-y-1.5">
              {validationErrors.map((error, index) => (
                <div key={error} data-testid={`triumvirate-builder-error-${index}`} className="text-sm text-red-300">
                  {error}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 space-y-4" data-testid="triumvirate-roster-list">
            {roster.length === 0 ? (
              <Empty label="No eligible gladiators are available for Ludus Trias right now." />
            ) : (
              <div className="grid gap-4 xl:grid-cols-2">
                {roster.map((fighter) => (
                  <FighterRow
                    key={fighter.id}
                    fighter={fighter}
                    selected={selectedIds.includes(fighter.id)}
                    lane={laneMap[fighter.id] || "left"}
                    onToggle={() => toggleFighter(fighter)}
                    onLaneChange={(laneValue) => setLaneMap((current) => ({ ...current, [fighter.id]: laneValue }))}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="triumvirate-save-squad-button" type="button" disabled={saving || validationErrors.length > 0} onClick={saveSquad} className={btn()}>
              {saving ? "Saving…" : "Save Squad"}
            </button>
            <button data-testid="triumvirate-reset-selection-button" type="button" onClick={() => { setSelectedIds([]); setLaneMap({}); }} className={btn("ghost")}>
              Clear Selection
            </button>
            <Link data-testid="triumvirate-builder-opponents-link" to="/ludus-trias/opponents" className={btn("ghost")}>
              Browse Opponents
            </Link>
            <Link data-testid="triumvirate-builder-ladder-link" to="/ludus-trias/ladder" className={btn("ghost")}>
              View Ladder
            </Link>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <div data-testid="triumvirate-saved-squad-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 data-testid="triumvirate-squad-name-display" className="font-cinzel text-2xl text-amber-200">{squad?.squad_name || "Ludus Trias Squad"}</h3>
                  <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-saved-badge" />
                </div>
                <p className="mt-1 text-sm text-zinc-500">This same saved squad is used for both offense and defense, and its score always updates live.</p>
              </div>
              <button data-testid="triumvirate-delete-squad-button" type="button" onClick={deleteSquad} disabled={!squad?.exists || deleting} className={btn("ghost")}>
                {deleting ? "Clearing…" : "Clear Squad"}
              </button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="border border-zinc-800 bg-zinc-950/55 p-3 rounded-sm">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Average squad score</div>
                <div data-testid="triumvirate-live-score" className="mt-2 font-cinzel text-2xl text-amber-300 tabular-nums">{Number(squad?.live_score || 0).toLocaleString()}</div>
              </div>
              <div className="border border-zinc-800 bg-zinc-950/55 p-3 rounded-sm">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Status</div>
                <div data-testid="triumvirate-squad-status" className={`mt-2 font-cinzel text-lg ${squad?.valid ? "text-emerald-300" : "text-red-300"}`}>
                  {squad?.status_label || "No squad saved"}
                </div>
              </div>
              <div className="border border-zinc-800 bg-zinc-950/55 p-3 rounded-sm">
                <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Last updated</div>
                <div data-testid="triumvirate-squad-updated" className="mt-2 text-sm text-zinc-300">{formatDate(squad?.updated_at)}</div>
              </div>
            </div>

            {squad?.validation_errors?.length > 0 && (
              <div data-testid="triumvirate-saved-errors" className="mt-4 border border-red-900/45 bg-red-950/20 p-4 rounded-sm space-y-2">
                {squad.validation_errors.map((error, index) => (
                  <p key={error} data-testid={`triumvirate-saved-error-${index}`} className="text-sm text-red-300">{error}</p>
                ))}
              </div>
            )}

            <div className="mt-4">
              <SquadMembers members={squad?.gladiators || []} testIdPrefix="triumvirate-saved" />
            </div>
          </div>

          <div>
            <div data-testid="triumvirate-rewards-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-cinzel text-2xl text-amber-200">Season Rewards</div>
                  <div className="mt-1 text-sm text-zinc-500">Top monthly ladder rewards for the unified Ludus Trias mode.</div>
                </div>
                <Link data-testid="triumvirate-rewards-archive-link" to="/ludus-trias/archive" className={btn("ghost")}>
                  View Archive
                </Link>
              </div>
              <div className="mt-4 space-y-3" data-testid="triumvirate-rewards-list">
                {selectedRewards.map((reward) => (
                  <div key={`overall-${reward.rank}`} data-testid={`triumvirate-reward-tier-overall-${reward.rank}`} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-cinzel text-lg text-amber-200">Rank #{reward.rank}</div>
                        <div className="mt-1 text-sm text-zinc-400">{reward.title}</div>
                      </div>
                      <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId={`triumvirate-reward-badge-overall-${reward.rank}`} />
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3 text-sm text-zinc-300">
                      <div data-testid={`triumvirate-reward-gold-overall-${reward.rank}`}>Gold: {Number(reward.gold || 0).toLocaleString()}</div>
                      <div data-testid={`triumvirate-reward-follis-overall-${reward.rank}`}>Follis: {Number(reward.follis || 0).toLocaleString()}</div>
                      <div data-testid={`triumvirate-reward-chest-overall-${reward.rank}`}>Chest: {reward.chest_item_id ? `${reward.chest_item_id} × ${reward.chest_qty}` : "None"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}